<template>
  <BaseModal
    :is-open="isOpen"
    title="Report an Issue"
    :show-actions="true"
    confirm-text="Send Report"
    cancel-text="Cancel"
    :loading="isSubmitting"
    :confirm-disabled="!issueType"
    max-width="500px"
    @confirm="handleSubmit"
    @close="handleClose"
  >
    <div class="report-issue-content">
      <p class="report-description">
        Let us know what's happening so we can help resolve the issue.
      </p>

      <!-- Issue Type Selection -->
      <div class="form-group">
        <label for="issue-type" class="form-label">What's happening?</label>
        <select
          id="issue-type"
          v-model="issueType"
          class="form-select"
        >
          <option value="">Select an issue type...</option>
          <option value="cant_discard">I can't discard a card</option>
          <option value="cant_draw">I can't draw cards</option>
          <option value="cant_meld">I can't create or add to melds</option>
          <option value="wrong_turn">It says it's not my turn</option>
          <option value="stuck">The game seems stuck</option>
          <option value="display">Cards/UI not showing correctly</option>
          <option value="sync">My game state seems out of sync</option>
          <option value="other">Other issue</option>
        </select>
      </div>

      <!-- Additional Details -->
      <div class="form-group">
        <label for="description" class="form-label">Additional details (optional)</label>
        <textarea
          id="description"
          v-model="description"
          class="form-textarea"
          rows="3"
          placeholder="Describe what you were trying to do..."
          maxlength="500"
        ></textarea>
        <span class="char-count">{{ description.length }}/500</span>
      </div>

      <!-- Context Info (for debugging) -->
      <div class="context-info">
        <span class="material-symbols-outlined">info</span>
        <span>Your current game state will be included to help us debug the issue.</span>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="submitMessage" :class="['submit-message', submitSuccess ? 'success' : 'error']">
        <span class="material-symbols-outlined">
          {{ submitSuccess ? 'check_circle' : 'error' }}
        </span>
        <span>{{ submitMessage }}</span>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * ReportIssueModal Component
 *
 * Allows players to report issues they encounter during the game.
 * Captures the issue type, description, and current game context
 * for debugging purposes.
 */

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  submit: [data: { issueType: string; description: string }]
}>()

// Form state
const issueType = ref('')
const description = ref('')
const isSubmitting = ref(false)
const submitMessage = ref('')
const submitSuccess = ref(false)

// Reset form when modal opens
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    issueType.value = ''
    description.value = ''
    submitMessage.value = ''
    submitSuccess.value = false
  }
})

function handleClose(): void {
  emit('close')
}

async function handleSubmit(): Promise<void> {
  if (!issueType.value) return

  isSubmitting.value = true
  submitMessage.value = ''

  try {
    emit('submit', {
      issueType: issueType.value,
      description: description.value,
    })

    // Show success message
    submitSuccess.value = true
    submitMessage.value = 'Issue reported successfully. We\'ll look into it!'

    // Auto-close after success
    setTimeout(() => {
      handleClose()
    }, 2000)
  } catch (error) {
    submitSuccess.value = false
    submitMessage.value = 'Failed to submit report. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.report-issue-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.report-description {
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
}

/* Form Groups */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Select Dropdown */
.form-select {
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.5)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.25rem;
  padding-right: 2.5rem;
}

.form-select:hover {
  border-color: rgba(232, 197, 71, 0.4);
  background-color: rgba(255, 255, 255, 0.08);
}

.form-select:focus {
  outline: none;
  border-color: rgba(232, 197, 71, 0.6);
  box-shadow: 0 0 0 3px rgba(232, 197, 71, 0.15);
}

.form-select option {
  background: #1a2f3f;
  color: rgba(255, 255, 255, 0.9);
}

/* Textarea */
.form-textarea {
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s;
}

.form-textarea:hover {
  border-color: rgba(232, 197, 71, 0.4);
  background-color: rgba(255, 255, 255, 0.08);
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(232, 197, 71, 0.6);
  box-shadow: 0 0 0 3px rgba(232, 197, 71, 0.15);
}

.form-textarea::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.char-count {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  text-align: right;
}

/* Context Info */
.context-info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: rgba(232, 197, 71, 0.1);
  border: 1px solid rgba(232, 197, 71, 0.2);
  border-radius: 10px;
  font-size: 0.8125rem;
  color: rgba(232, 197, 71, 0.9);
}

.context-info .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
}

/* Submit Message */
.submit-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
}

.submit-message.success {
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: rgba(34, 197, 94, 1);
}

.submit-message.error {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: rgba(239, 68, 68, 1);
}

.submit-message .material-symbols-outlined {
  font-size: 1.25rem;
}
</style>
